import { useTheme } from '@ihs-widget/stores/useTheme';
import { T } from '@ihs-widget/utils/enums';
import { useRef, useEffect, useState } from 'react';
import { OverviewScoreboardScore } from '@ihs-widget/components/StatsConsole/Widgets/OverviewWidget/parts/OverviewScoreboardScore';
import scoreboardBG from '@ihs-widget/assets/dynamicColors/scoreboardBG/scoreboardBG.webp';
import { useOverviewDataProcessing } from '@ihs-widget/components/StatsConsole/Widgets/OverviewWidget/hooks/useOverviewDataProcessing';
import { OverviewScoreboardTeamDynamic } from '../../../OverviewWidget/parts/OverviewScoreboardTeamDynamic';
import { useScoreboardWidthDynamic } from './hooks/useScoreboardWidthDynamic';
import type { MatchClockData } from '@ihs-widget/components/StatsConsole/Layout/StandardAndExpandedStatsConsole/StandardAndExpandedStatsConsole';

interface IOverviewWidgetProps {
    eventId: string | null;
    overviewData: any;
    isMVP?: boolean;
    dontShowMarginTop?: boolean;
    matchTime?: MatchClockData;
    homeTeamName?: string | null;
    awayTeamName?: string | null;
}

const ScoreBoardScoreAndTimeDynamic = ({ eventId, overviewData, isMVP = true, dontShowMarginTop = true, matchTime, homeTeamName, awayTeamName }: IOverviewWidgetProps) => {
    const { theme } = useTheme();
    const [clockWidth, setClockWidth] = useState(0);

    const {
        matchEnded,
        isHalfTimeFirstEvent,
        isEventAfterHalfTime,
        isFullTimeFirstEvent,
        isEventAfterFullTime,
        isExtraTimeFirstEvent,
        isPenaltiesFirstEvent,
        isEventAfterPenalties,
        matchHomeScore,
        matchAwayScore,
        overviewDataSorted,
        penaltyResult,
        penaltiesAreStarted,
        shouldShowPenaltyAfterMatchEnd
    } = useOverviewDataProcessing({ overviewData, eventId });

    const overviewScoreboardHomeTeamRef = useRef<HTMLDivElement>(null);
    const overviewScoreboardScoreRef = useRef<HTMLDivElement>(null);
    const overviewScoreboardAwayTeamRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (overviewScoreboardScoreRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setClockWidth(entry.contentRect.width);
                }
            });
            
            resizeObserver.observe(overviewScoreboardScoreRef.current);
            
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    const { teamWidth, maxScoreboardWidth } = useScoreboardWidthDynamic({ 
        homeTeam: homeTeamName ?? '', 
        awayTeam: awayTeamName ?? '', 
        staticPadding: 64, 
        clockWidth,
        containerPadding: 24
    });

    return (
        <div className={`h-[88px] min-h-[88px] max-h-[88px] w-full flex justify-between relative ${isHalfTimeFirstEvent || isFullTimeFirstEvent || isExtraTimeFirstEvent || isPenaltiesFirstEvent || dontShowMarginTop ? 'mb-0' : 'mb-[8px]'}`}>
            <div className=' h-full w-full flex justify-center items-center bg-transparent absolute top-0 left-0 right-0 z-10 '
                style={{ backgroundImage: `url(${scoreboardBG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className='flex justify-around items-center h-[68px] z-20 bg-[#2B2B2B] w-fit-content min-w-fit rounded-[12px] px-[12px] overflow-hidden'
                     style={{ maxWidth: `${maxScoreboardWidth}px` }}>
                    <div ref={overviewScoreboardHomeTeamRef} className="flex justify-center h-full" style={{ width: `${teamWidth+8}px`, minWidth: `${teamWidth+8}px` }}>
                        <OverviewScoreboardTeamDynamic
                            team={theme === T.DARK ? 'team_a_dark' : 'team_a'}
                            homeTeam={homeTeamName ?? ''}
                            awayTeam={awayTeamName ?? ''}
                        />
                    </div>
                    <div ref={overviewScoreboardScoreRef} className="h-full shrink-0 mx-4">
                        <OverviewScoreboardScore
                            away_score={matchAwayScore}
                            home_score={matchHomeScore}
                            isEnded={matchEnded || matchTime?.isFullTime}
                            isHalfTimeFirstEvent={isHalfTimeFirstEvent}
                            isFullTimeFirstEvent={isFullTimeFirstEvent || isEventAfterFullTime}
                            isEventAfterFullTime={isEventAfterFullTime}
                            isExtraTimeFirstEvent={isExtraTimeFirstEvent}
                            isPenaltiesFirstEvent={isPenaltiesFirstEvent || isEventAfterPenalties}
                            isEventAfterPenalties={isEventAfterPenalties}
                            penaltyResult={penaltyResult}
                            penaltiesAreStarted={penaltiesAreStarted}
                            shouldShowPenaltyAfterMatchEnd={shouldShowPenaltyAfterMatchEnd}
                            matchClockData={matchTime ? {
                                minutes_played: matchTime.minutes,
                                seconds_played: matchTime.seconds,
                                is_running: matchTime.isRunning,
                                home_score: matchHomeScore,
                                away_score: matchAwayScore,
                                isEnded: matchEnded,
                                isFullTime: matchTime.isFullTime,
                                isHalfTime: matchTime.isHalfTime,
                                isExtraTimeFirstHalf: matchTime.isExtraTimeFirstHalf,
                                isExtraTimeSecondHalf: matchTime.isExtraTimeSecondHalf,
                                isHalfTimeOfExtraTime: matchTime.isHalfTimeOfExtraTime,
                                isPenalties: matchTime.isPenalties,
                                isPreMatch: matchTime.isPreMatch,
                                isEndOfSecondHalf: matchTime.isEndOfSecondHalf,
                                isEndOfExtraTime: matchTime.isEndOfExtraTime,
                                minute: matchTime.minute,
                                time_start: matchTime.time_start,
                                currentPeriod: matchTime.currentPeriod
                            } : overviewDataSorted?.match_clock}
                        />
                    </div>
                    <div ref={overviewScoreboardAwayTeamRef} className="flex justify-center h-full" style={{ width: `${teamWidth+8}px`, minWidth: `${teamWidth+8}px` }}>
                        <OverviewScoreboardTeamDynamic
                            team={theme === T.DARK ? 'team_b_dark' : 'team_b'}
                            homeTeam={homeTeamName ?? ''}
                            awayTeam={awayTeamName ?? ''}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ScoreBoardScoreAndTimeDynamic;